import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import { CircularProgress, Button, Grid, Box } from "@mui/material";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import PersonIcon from "@mui/icons-material/Person";
import StarIcon from "@mui/icons-material/Star";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import FormInput from "../PlanManagement/components/FormInput";
import api from "utils/api";
import { toast } from "react-toastify";
import ArgonInput from "components/ArgonInput";

function EHCPlanManagement() {
  const [planData, setPlanData] = useState({
    planName: "",
    title: "",
    subTitle: "",
    description: "",
    price: "",
    MRP: "",
    discountPrice: "",
    avgRating: 0,
    ratingCount: 0,
    studentEnrolled: 0,
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);

  // Fetch EHC Plan
  const fetchEHCPlan = async () => {
    setFetchLoading(true);
    try {
      const response = await api.get("/ehc-plan");
      if (response.data.success) {
        const plan = response.data.ehcPlan;
        setPlanData({
          planName: plan.planName || "",
          title: plan.title || "",
          subTitle: plan.subTitle || "",
          description: plan.description || "",
          price: plan.price?.toString() || "",
          MRP: plan.MRP?.toString() || "",
          discountPrice: plan.discountPrice?.toString() || "",
          avgRating: plan.avgRating?.toString() || "0",
          ratingCount: plan.ratingCount?.toString() || "0",
          studentEnrolled: plan.studentEnrolled?.toString() || "0",
        });
      }
    } catch (error) {
      console.error("Error fetching EHC Plan:", error);
      toast.error(error.response?.data?.message || "Failed to fetch EHC Plan");
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    fetchEHCPlan();
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPlanData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle update
  const handleUpdate = async () => {
    // Validation
    if (!planData.planName || !planData.title || !planData.subTitle || !planData.description) {
      toast.error("Please fill all required fields");
      return;
    }

    const price = Number(planData.price);
    const mrp = Number(planData.MRP);
    const discount = Number(planData.discountPrice);
    const avgRating = Number(planData.avgRating);
    const ratingCount = Number(planData.ratingCount);
    const studentEnrolled = Number(planData.studentEnrolled);

    if (isNaN(price) || isNaN(mrp) || isNaN(discount) || price < 0 || mrp < 0 || discount < 0) {
      toast.error("Please enter valid positive numbers for price fields");
      return;
    }

    if (isNaN(avgRating) || avgRating < 0 || avgRating > 5) {
      toast.error("Average rating must be between 0 and 5");
      return;
    }

    if (isNaN(ratingCount) || ratingCount < 0) {
      toast.error("Rating count must be a positive number");
      return;
    }

    if (isNaN(studentEnrolled) || studentEnrolled < 0) {
      toast.error("Student enrolled must be a positive number");
      return;
    }

    if (price > mrp) {
      toast.error("Price cannot be greater than MRP");
      return;
    }

    setLoading(true);
    try {
      const response = await api.put("/ehc-plan", {
        planName: planData.planName,
        title: planData.title,
        subTitle: planData.subTitle,
        description: planData.description,
        price: price,
        MRP: mrp,
        discountPrice: discount,
        avgRating: avgRating,
        ratingCount: ratingCount,
        studentEnrolled: studentEnrolled,
      });

      if (response.data.success) {
        toast.success("EHC Plan updated successfully!");
        fetchEHCPlan(); // Refresh data
      }
    } catch (error) {
      console.error("Error updating EHC Plan:", error);
      toast.error(error.response?.data?.message || "Failed to update EHC Plan");
    } finally {
      setLoading(false);
    }
  };

  const discountAmount = Number(planData.discountPrice) || 0;
  const discountPercentage =
    planData.discountPrice && planData.price
      ? Math.round(
          (Number(planData.discountPrice) /
            (Number(planData.price) + Number(planData.discountPrice))) *
            100
        )
      : 0;

  const displayAvgRating = Number(planData.avgRating) || 0;
  const displayRatingCount = Number(planData.ratingCount) || 0;
  const displayStudentEnrolled = Number(planData.studentEnrolled) || 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox
                display="flex"
                alignItems="center"
                mb={3}
                borderBottom="1px solid #ccc"
                pb={2}
              >
                <WorkspacePremiumIcon sx={{ fontSize: 32, mr: 2, color: "#1976d2" }} />
                <ArgonTypography variant="h5" fontWeight="bold">
                  EHC Plan Management
                </ArgonTypography>
              </ArgonBox>

              {fetchLoading ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" py={5}>
                  <CircularProgress />
                </ArgonBox>
              ) : (
                <>
                  {/* Stats Summary Cards */}
                  <ArgonBox mb={4}>
                    <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
                      Plan Statistics
                    </ArgonTypography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Card sx={{ p: 2, backgroundColor: "#e3f2fd" }}>
                          <Box display="flex" alignItems="center">
                            <PersonIcon sx={{ fontSize: 40, color: "#1976d2", mr: 2 }} />
                            <Box>
                              <ArgonTypography variant="caption" color="text" fontWeight="bold">
                                Students Enrolled
                              </ArgonTypography>
                              <ArgonTypography variant="h4" fontWeight="bold" color="primary">
                                {displayStudentEnrolled}
                              </ArgonTypography>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Card sx={{ p: 2, backgroundColor: "#fff3e0" }}>
                          <Box display="flex" alignItems="center">
                            <StarIcon sx={{ fontSize: 40, color: "#ff9800", mr: 2 }} />
                            <Box>
                              <ArgonTypography variant="caption" color="text" fontWeight="bold">
                                Avg Rating
                              </ArgonTypography>
                              <ArgonTypography variant="h4" fontWeight="bold" color="warning">
                                {displayAvgRating.toFixed(1)} ({displayRatingCount} reviews)
                              </ArgonTypography>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Card sx={{ p: 2, backgroundColor: "#e8f5e9" }}>
                          <Box display="flex" alignItems="center">
                            <CurrencyRupeeIcon sx={{ fontSize: 40, color: "#4caf50", mr: 2 }} />
                            <Box>
                              <ArgonTypography variant="caption" color="text" fontWeight="bold">
                                Discount
                              </ArgonTypography>
                              <ArgonTypography variant="h4" fontWeight="bold" color="success">
                                ₹{discountAmount} ({discountPercentage}% OFF)
                              </ArgonTypography>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    </Grid>
                  </ArgonBox>

                  {/* Edit Form */}
                  <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
                    Update Plan Details
                  </ArgonTypography>
                  <Grid container spacing={3}>
                    {/* Basic Information Section */}
                    <Grid item xs={12}>
                      <ArgonBox p={2} mb={2} sx={{ backgroundColor: "#f5f5f5", borderRadius: 2 }}>
                        <ArgonTypography variant="body2" fontWeight="bold" mb={2}>
                          Basic Information
                        </ArgonTypography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <ArgonTypography
                              variant="caption"
                              fontWeight="bold"
                              color="text"
                              mb={1}
                            >
                              Plan Name *
                            </ArgonTypography>
                            <ArgonInput
                              label="Plan Name"
                              name="planName"
                              value={planData.planName}
                              onChange={handleInputChange}
                              placeholder="Enter plan name"
                              required
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <ArgonTypography
                              variant="caption"
                              fontWeight="bold"
                              color="text"
                              mb={1}
                            >
                              Title *
                            </ArgonTypography>
                            <ArgonInput
                              label="Title"
                              name="title"
                              value={planData.title}
                              onChange={handleInputChange}
                              placeholder="Enter title"
                              required
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <ArgonTypography
                              variant="caption"
                              fontWeight="bold"
                              color="text"
                              mb={1}
                            >
                              Sub Title *
                            </ArgonTypography>
                            <ArgonInput
                              label="Sub Title"
                              name="subTitle"
                              value={planData.subTitle}
                              onChange={handleInputChange}
                              placeholder="Enter sub title"
                              required
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <ArgonTypography
                              variant="caption"
                              fontWeight="bold"
                              color="text"
                              mb={1}
                            >
                              Description *
                            </ArgonTypography>
                            <ArgonInput
                              label="Description"
                              name="description"
                              value={planData.description}
                              onChange={handleInputChange}
                              placeholder="Enter description"
                              multiline
                              rows={4}
                              required
                            />
                          </Grid>
                        </Grid>
                      </ArgonBox>
                    </Grid>

                    {/* Pricing Section */}
                    <Grid item xs={12}>
                      <ArgonBox p={2} mb={2} sx={{ backgroundColor: "#f5f5f5", borderRadius: 2 }}>
                        <ArgonTypography variant="body2" fontWeight="bold" mb={2}>
                          Pricing Details
                        </ArgonTypography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <FormInput
                              label="MRP (₹)"
                              name="MRP"
                              type="number"
                              value={planData.MRP}
                              onChange={handleInputChange}
                              placeholder="Enter MRP"
                              startIcon={<CurrencyRupeeIcon fontSize="small" />}
                              required
                            />
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <FormInput
                              label="Selling Price (₹)"
                              name="price"
                              type="number"
                              value={planData.price}
                              onChange={handleInputChange}
                              placeholder="Enter selling price"
                              startIcon={<CurrencyRupeeIcon fontSize="small" />}
                              required
                            />
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <FormInput
                              label="Discount Amount (₹)"
                              name="discountPrice"
                              type="number"
                              value={planData.discountPrice}
                              onChange={handleInputChange}
                              placeholder="Enter discount amount"
                              startIcon={<CurrencyRupeeIcon fontSize="small" />}
                              required
                            />
                          </Grid>
                        </Grid>
                      </ArgonBox>
                    </Grid>

                    {/* Stats & Analytics Section */}
                    <Grid item xs={12}>
                      <ArgonBox p={2} mb={2} sx={{ backgroundColor: "#f5f5f5", borderRadius: 2 }}>
                        <ArgonTypography variant="body2" fontWeight="bold" mb={2}>
                          Statistics & Analytics
                        </ArgonTypography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <FormInput
                              label="Average Rating (0-5)"
                              name="avgRating"
                              type="number"
                              value={planData.avgRating}
                              onChange={handleInputChange}
                              placeholder="Enter average rating"
                              startIcon={<StarIcon fontSize="small" />}
                              helperText="Enter value between 0 and 5"
                            />
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <FormInput
                              label="Rating Count"
                              name="ratingCount"
                              type="number"
                              value={planData.ratingCount}
                              onChange={handleInputChange}
                              placeholder="Enter rating count"
                              startIcon={<StarIcon fontSize="small" />}
                            />
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <FormInput
                              label="Students Enrolled"
                              name="studentEnrolled"
                              type="number"
                              value={planData.studentEnrolled}
                              onChange={handleInputChange}
                              placeholder="Enter students enrolled"
                              startIcon={<PersonIcon fontSize="small" />}
                            />
                          </Grid>
                        </Grid>
                      </ArgonBox>
                    </Grid>

                    <Grid item xs={12}>
                      <ArgonBox display="flex" justifyContent="flex-end">
                        <Button
                          variant="contained"
                          color="primary"
                          size="medium"
                          onClick={handleUpdate}
                          disabled={loading}
                          sx={{ minWidth: 200, color: "#fff" }}
                        >
                          {loading ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            "Update EHC Plan"
                          )}
                        </Button>
                      </ArgonBox>
                    </Grid>
                  </Grid>
                </>
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EHCPlanManagement;
